import { useState, useEffect, useRef } from 'react';
import { useKleverWallet } from '../hooks/useKleverWallet';
import { useConfig, Plan, PaymentToken } from '../context/ConfigContext';

interface PaymentFlowProps {
  plan: Plan;
  duration: number;
  onSuccess: (txHash: string, apiKey: string) => void;
  onCancel: () => void;
}

type PaymentStep = 'select-token' | 'confirm' | 'processing' | 'success' | 'error';

// Bundle A (2026-05-24): the API now delivers the plaintext api_key exactly once on
// the first successful poll of /subscription/check-payment. After delivery the column
// is NULLed in the database (migration 069). The portal must surface the key to the
// buyer in this `success` step BEFORE handing control to the Dashboard, which can no
// longer recover the plaintext.

interface PendingPayment {
  id: string;
  memo: string;
  amount: number;
  token: string;
  expiresAt: string;
  receiverWallet: string;
}

export function PaymentFlow({ plan, duration, onSuccess, onCancel }: PaymentFlowProps) {
  const { isConnected, address, sendPayment } = useKleverWallet();
  const config = useConfig();
  const [step, setStep] = useState<PaymentStep>('select-token');
  const [selectedToken, setSelectedToken] = useState<PaymentToken>(
    config.paymentTokens[0] || { ticker: 'USDC-1LN4', name: 'USDC', precision: 6 }
  );
  const [pendingPayment, setPendingPayment] = useState<PendingPayment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [issuedApiKey, setIssuedApiKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [keySaved, setKeySaved] = useState(false);

  const discount = config.discounts[duration] || 0;
  const monthlyPrice = plan.priceUsd * (1 - discount);
  const totalPrice = monthlyPrice * duration;

  // Create pending payment on server
  const createPendingPayment = async () => {
    if (!address) return;

    try {
      const response = await fetch(`${config.apiUrl}/subscription/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: address,
          plan_slug: plan.id,
          months: duration,
          payment_token: selectedToken.ticker,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create payment request');
      }

      const result = await response.json();
      setPendingPayment({
        id: result.data.payment_memo,
        memo: result.data.payment_memo,
        amount: result.data.amount_token,
        token: result.data.payment_token,
        expiresAt: result.data.expires_at,
        receiverWallet: result.data.receiver_wallet,
      });
      setStep('confirm');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create payment');
      setStep('error');
    }
  };

  // Execute the payment
  const executePayment = async () => {
    if (!pendingPayment || !address) return;

    setStep('processing');
    setError(null);

    try {
      // Use the receiver wallet from the payment response (most up-to-date)
      const hash = await sendPayment(
        pendingPayment.receiverWallet,
        pendingPayment.amount,
        pendingPayment.token,
        pendingPayment.memo
      );

      setTxHash(hash);

      // Poll for payment confirmation. The API delivers the plaintext api_key exactly
      // once on the first poll that finds the payment completed. We hold onto it in
      // local state and render it in the success step — the buyer must save it before
      // continuing to the Dashboard (the Dashboard cannot recover plaintext after this).
      const apiKey = await pollForConfirmation(pendingPayment.id, hash);

      if (apiKey) {
        setIssuedApiKey(apiKey);
        setStep('success');
      } else {
        throw new Error('Payment not confirmed within timeout');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed');
      setStep('error');
    }
  };

  const copyIssuedApiKey = () => {
    if (!issuedApiKey) return;
    navigator.clipboard.writeText(issuedApiKey).then(
      () => {
        setKeyCopied(true);
        setTimeout(() => setKeyCopied(false), 2000);
      },
      () => {
        // Clipboard may be unavailable on http: origins; the key remains visible.
      }
    );
  };

  const handleContinueToDashboard = () => {
    if (issuedApiKey && txHash) {
      onSuccess(txHash, issuedApiKey);
    }
  };

  // Session 1 (2026-05-25, closes PT-P2-10): AbortController so unmount
  // (user clicks Cancel mid-poll) terminates the in-flight fetch + the
  // 10s sleep instead of running the full 5-min poll loop to completion.
  // Without this we'd do up to 30 useless fetches after the component
  // unmounts, plus potentially setState on an unmounted component.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      // Component unmount — abort any in-flight poll.
      abortRef.current?.abort();
    };
  }, []);

  // Poll the server to check if payment was processed
  const pollForConfirmation = async (paymentMemo: string, _hash: string): Promise<string | null> => {
    const maxAttempts = 30; // 5 minutes with 10s intervals
    const interval = 10000; // 10 seconds

    // Replace any prior controller (defensive — only one poll should be live).
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    for (let i = 0; i < maxAttempts; i++) {
      if (controller.signal.aborted) {
        return null;
      }
      try {
        const response = await fetch(
          `${config.apiUrl}/subscription/check-payment/${encodeURIComponent(paymentMemo)}`,
          { signal: controller.signal }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.data.status === 'completed' && result.data.api_key) {
            return result.data.api_key;
          }
        }
      } catch (err) {
        // Abort surfaces as DOMException name 'AbortError' — exit cleanly.
        if (err instanceof DOMException && err.name === 'AbortError') {
          return null;
        }
        // Other errors: continue polling.
      }

      // Sleep with abort support so cancel terminates within the interval.
      try {
        await new Promise<void>((resolve, reject) => {
          const timer = setTimeout(resolve, interval);
          controller.signal.addEventListener('abort', () => {
            clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
          });
        });
      } catch {
        return null;
      }
    }

    return null;
  };

  if (!isConnected) {
    return (
      <div className="payment-flow">
        <div className="error-message">Please connect your wallet first</div>
        <button onClick={onCancel} className="btn btn-outline">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="payment-flow">
      {step === 'select-token' && (
        <>
          <h3>Select Payment Token</h3>
          <div className="token-options">
            {config.paymentTokens.map(token => (
              <button
                key={token.ticker}
                className={`token-option ${selectedToken.ticker === token.ticker ? 'selected' : ''}`}
                onClick={() => setSelectedToken(token)}
              >
                <span className="token-name">{token.name}</span>
                <span className="token-ticker">{token.ticker}</span>
              </button>
            ))}
          </div>

          <div className="payment-summary">
            <div className="summary-row">
              <span>Plan</span>
              <span>{plan.name}</span>
            </div>
            <div className="summary-row">
              <span>Duration</span>
              <span>{duration} month{duration > 1 ? 's' : ''}</span>
            </div>
            {discount > 0 && (
              <div className="summary-row discount">
                <span>Discount</span>
                <span>-{(discount * 100).toFixed(0)}%</span>
              </div>
            )}
            <div className="summary-row total">
              <span>Total</span>
              <span>{totalPrice.toFixed(2)} {selectedToken.name}</span>
            </div>
          </div>

          <div className="button-group">
            <button onClick={onCancel} className="btn btn-outline">
              Cancel
            </button>
            <button onClick={createPendingPayment} className="btn btn-primary">
              Continue
            </button>
          </div>
        </>
      )}

      {step === 'confirm' && pendingPayment && (
        <>
          <h3>Confirm Payment</h3>
          <div className="payment-details">
            <p>You will send:</p>
            <div className="amount-display">
              {pendingPayment.amount.toFixed(2)} {selectedToken.name}
            </div>
            <p className="memo-note">
              Payment memo: <code>{pendingPayment.memo}</code>
            </p>
            <p className="expiry-note">
              This payment request expires at{' '}
              {new Date(pendingPayment.expiresAt).toLocaleTimeString()}
            </p>
          </div>

          <div className="button-group">
            <button onClick={() => setStep('select-token')} className="btn btn-outline">
              Back
            </button>
            <button onClick={executePayment} className="btn btn-primary">
              Pay with Klever
            </button>
          </div>
        </>
      )}

      {step === 'processing' && (
        <div className="processing-state">
          <div className="spinner"></div>
          <h3>Processing Payment</h3>
          <p>Please confirm the transaction in your Klever wallet...</p>
          {txHash && (
            <p className="tx-hash">
              Transaction: {txHash.slice(0, 12)}...{txHash.slice(-8)}
            </p>
          )}
        </div>
      )}

      {step === 'success' && (
        <div className="success-state">
          <div className="success-icon">✓</div>
          <h3>Payment Successful!</h3>
          <p>Your API subscription is now active.</p>

          {issuedApiKey && (
            <div className="api-key-delivery">
              <div className="api-key-warning">
                <strong>Save this API key now.</strong> It is shown only once and
                cannot be retrieved again — if you lose it you must contact support
                to rotate it.
              </div>
              <div className="api-key-box">
                <code>{issuedApiKey}</code>
                <button
                  type="button"
                  onClick={copyIssuedApiKey}
                  className="btn btn-sm btn-outline"
                >
                  {keyCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <label className="api-key-ack">
                <input
                  type="checkbox"
                  checked={keySaved}
                  onChange={e => setKeySaved(e.target.checked)}
                />
                <span>I have saved my API key in a safe place.</span>
              </label>
              <button
                type="button"
                onClick={handleContinueToDashboard}
                className="btn btn-primary"
                disabled={!keySaved}
              >
                Continue to Dashboard
              </button>
            </div>
          )}
        </div>
      )}

      {step === 'error' && (
        <div className="error-state">
          <div className="error-icon">!</div>
          <h3>Payment Failed</h3>
          <p>{error}</p>
          <div className="button-group">
            <button onClick={onCancel} className="btn btn-outline">
              Cancel
            </button>
            <button onClick={() => setStep('select-token')} className="btn btn-primary">
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
